import React, { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const db = () => serviceRoleClient || supabase;

const phoneVariants = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  const phone10 = digits.length >= 10 ? digits.slice(-10) : digits;
  return [...new Set([phone, phone10, phone10 ? `91${phone10}` : null, digits || null].filter(Boolean))];
};

const partnerLabel = (m, userName) => {
  const shop = String(m?.shop_name || m?.mechanic_name || m?.name || '').trim();
  const person = String(userName || m?.mechanic_name || '').trim();
  if (shop && person && shop.toLowerCase() !== person.toLowerCase()) return `${shop} · ${person}`;
  return shop || person || 'Partner';
};

const fmtDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ChatBubble = ({ mine, name, text, at, image }) => (
  <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
    <div
      className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
        mine ? 'bg-teal-600 text-white' : 'bg-white text-slate-800 border border-slate-200'
      }`}
    >
      {name ? (
        <p className={`mb-0.5 text-[11px] font-semibold ${mine ? 'text-teal-100' : 'text-slate-400'}`}>
          {name}
        </p>
      ) : null}
      {image ? (
        <a href={image} target="_blank" rel="noreferrer" className="mb-1 block">
          <img src={image} alt="" className="max-h-36 rounded-md object-cover" />
        </a>
      ) : null}
      {text ? <p className="whitespace-pre-wrap break-words">{text}</p> : null}
      <p className={`mt-1 text-[10px] ${mine ? 'text-teal-100' : 'text-slate-400'}`}>{fmtDate(at)}</p>
    </div>
  </div>
);

const waText = (m) => {
  const raw = String(m.content || '').trim();
  if (raw && raw !== '{}' && !raw.startsWith('{')) return raw;
  if (m.template_name) return `[Template] ${m.template_name}`;
  if (m.message_type && m.message_type !== 'text') return `[${m.message_type}]`;
  return raw || '—';
};

export default function ComplaintJobChat({ requestId, phone, embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [appMsgs, setAppMsgs] = useState([]);
  const [waMsgs, setWaMsgs] = useState([]);
  const [mechById, setMechById] = useState({});
  const [userById, setUserById] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!requestId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const client = db();
      const variants = phoneVariants(phone);
      const [appRes, waRes] = await Promise.all([
        client
          .from('rsa_job_messages')
          .select('id, sender_role, sender_user_id, message, message_type, media_url, created_at, mechanic_id')
          .eq('service_request_id', requestId)
          .order('created_at', { ascending: true })
          .limit(200),
        variants.length
          ? client
              .from('whatsapp_messages')
              .select('id, direction, content, message_type, media_url, template_name, created_at, source')
              .in('contact_number', variants)
              .order('created_at', { ascending: false })
              .limit(80)
              .then((r) => {
                if (r.error) {
                  return client
                    .from('whatsapp_messages')
                    .select('id, direction, content, message_type, media_url, created_at, source')
                    .in('contact_number', variants)
                    .order('created_at', { ascending: false })
                    .limit(80);
                }
                return r;
              })
          : Promise.resolve({ data: [] }),
      ]);
      if (cancelled) return;
      const msgs = Array.isArray(appRes.data) ? appRes.data : [];
      setAppMsgs(msgs);
      setWaMsgs(Array.isArray(waRes.data) ? [...waRes.data].reverse() : []);

      const mechIds = [...new Set(msgs.map((m) => m.mechanic_id).filter(Boolean))];
      const userIds = [
        ...new Set(
          msgs
            .filter((m) => m.sender_role === 'partner' && m.sender_user_id)
            .map((m) => m.sender_user_id),
        ),
      ];
      const names = {};
      const usersMap = {};
      if (mechIds.length) {
        const [{ data: mechs }, { data: users }] = await Promise.all([
          client.from('mechanics').select('id, shop_name, mechanic_name, number').in('id', mechIds),
          userIds.length
            ? client.from('users').select('id, name').in('id', userIds)
            : Promise.resolve({ data: [] }),
        ]);
        for (const mech of mechs || []) names[String(mech.id)] = mech;
        for (const u of users || []) usersMap[String(u.id)] = String(u.name || '').trim();
      }
      if (!cancelled) {
        setMechById(names);
        setUserById(usersMap);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [requestId, phone]);

  const chattingNames = [
    ...new Set(
      appMsgs
        .filter((m) => m.sender_role === 'partner')
        .map((m) => partnerLabel(mechById[String(m.mechanic_id)], userById[String(m.sender_user_id || '')])),
    ),
  ];

  return (
    <div className={embedded ? '' : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6'}>
      {!embedded ? (
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle size={20} className="text-teal-600" />
          <h2 className="text-xl font-bold text-gray-900">Customer chat</h2>
          <span className="rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
            App {appMsgs.length}
          </span>
          <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
            WhatsApp {waMsgs.length}
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="rounded-md bg-sky-100 px-2 py-0.5 text-[11px] font-bold text-sky-800">App {appMsgs.length}</span>
          <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
            WhatsApp {waMsgs.length}
          </span>
          {chattingNames.length ? (
            <span className="text-sm text-slate-600">Baat hui: {chattingNames.join(', ')}</span>
          ) : null}
        </div>
      )}
      {loading ? (
        <p className="text-sm text-gray-500">Loading chats…</p>
      ) : (
        <div className={`grid gap-3 ${embedded ? '' : 'md:grid-cols-2'}`}>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-sky-700">App chat (customer ↔ partner)</p>
            {appMsgs.length === 0 ? (
              <p className="text-sm text-gray-500">Is job pe app chat nahi hua.</p>
            ) : (
              <>
                {!embedded && chattingNames.length ? (
                  <p className="mb-2 text-sm font-semibold text-sky-800">Baat hui: {chattingNames.join(', ')}</p>
                ) : null}
                <div className={`${embedded ? 'max-h-56' : 'max-h-80'} space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-2`}>
                  {appMsgs.map((m) => (
                    <ChatBubble
                      key={m.id}
                      mine={m.sender_role === 'partner'}
                      name={
                        m.sender_role === 'partner'
                          ? partnerLabel(mechById[String(m.mechanic_id)], userById[String(m.sender_user_id || '')])
                          : 'Customer'
                      }
                      text={m.message}
                      image={m.media_url}
                      at={m.created_at}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-700">WhatsApp</p>
            {waMsgs.length === 0 ? (
              <p className="text-sm text-gray-500">Is number pe WhatsApp chat nahi mila.</p>
            ) : (
              <div className={`${embedded ? 'max-h-56' : 'max-h-80'} space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-2`}>
                {waMsgs.map((m) => (
                  <ChatBubble
                    key={m.id}
                    mine={m.direction === 'outbound'}
                    name={m.direction === 'outbound' ? 'RoadServe' : 'Customer'}
                    text={waText(m)}
                    image={m.media_url}
                    at={m.created_at}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
